import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Brain, Zap, Award } from 'lucide-react';
import { useTheme } from '@/components/layout/ThemeProvider';
import { FaWhatsapp } from 'react-icons/fa';
import { scrollToSection } from '@/lib/scroll';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/* ── Floating orbs — minimal for perf ── */
const ORBS = [
  { w: 320, h: 320, top: '8%', left: '-5%', color: 'rgba(13,148,136,0.12)', dur: 18 },
  { w: 240, h: 240, top: '60%', left: '88%', color: 'rgba(245,158,11,0.10)', dur: 22 },
  { w: 180, h: 180, top: '75%', left: '15%', color: 'rgba(6,182,212,0.08)', dur: 16 },
  { w: 140, h: 140, top: '20%', left: '78%', color: 'rgba(52,211,153,0.09)', dur: 20 },
];

const STAR_COUNT = 30;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const FeaturePill = ({ icon: Icon, label, color }) => (
  <motion.div
    whileHover={{ scale: 1.06, y: -3 }}
    transition={{ type: 'spring', stiffness: 340, damping: 20 }}
    className="flex flex-col items-center gap-3 group"
  >
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center
                 shadow-lg ring-1 ring-white/20 group-hover:shadow-xl
                 transition-shadow duration-300"
      style={{ background: color }}
    >
      <Icon className="w-7 h-7 text-white drop-shadow-sm" />
    </div>
    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider
                     group-hover:text-foreground transition-colors duration-200">
      {label}
    </span>
  </motion.div>
);

const StatBadge = ({ value, label }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-2xl md:text-3xl font-bold stat-value-gradient leading-none tracking-tight">
      {value}
    </span>
    <span className="text-eyebrow font-semibold text-muted-foreground uppercase tracking-[0.12em]">
      {label}
    </span>
  </div>
);

const Hero = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const stars = useMemo(
    () =>
      Array.from({ length: STAR_COUNT }, (_, i) => ({
        id: i,
        size: Math.random() * 1.5 + 0.8,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        opacity: Math.random() * 0.4 + 0.2,
        duration: `${Math.random() * 40 + 40}s`,
        delay: `${Math.random() * 8}s`,
      })),
    []
  );

  return (
    <section
      id="hero"
      className={cn(
        'relative flex min-h-screen items-center justify-center overflow-hidden',
        'pt-[var(--header-height)] pb-16 md:pb-20 lg:pb-24',
        'hero-gradient transition-colors duration-300'
      )}
      aria-label="Hero"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {/* Dot mesh texture */}
        <div className="absolute inset-0 bg-mesh opacity-70" />

        {stars.map((s) => (
          <div
            key={s.id}
            className="absolute rounded-full star-particle"
            style={{
              width: `${s.size}px`,
              height: `${s.size}px`,
              left: s.left,
              top: s.top,
              opacity: s.opacity,
              background: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.25)',
              animationName: 'starDrift',
              animationDuration: s.duration,
              animationDelay: s.delay,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
            }}
          />
        ))}
        {ORBS.map((o, i) => (
          <div
            key={i}
            className="absolute rounded-full floating-orb"
            style={{
              width: o.w,
              height: o.h,
              top: o.top,
              left: o.left,
              background: o.color,
              filter: 'blur(52px)',
              animationDuration: `${o.dur}s`,
              animationDelay: `${i * 1.2}s`,
            }}
          />
        ))}
        {/* Radial depth overlays */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 65% 55% at 28% 42%, rgba(13,148,136,0.12) 0%, transparent 70%), ' +
              'radial-gradient(ellipse 55% 50% at 72% 62%, rgba(245,158,11,0.09) 0%, transparent 70%), ' +
              'radial-gradient(ellipse 40% 35% at 50% 90%, rgba(13,148,136,0.06) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Content — max-width + centered, design-system spacing */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-hero-content text-center">
          {/* Eyebrow / announcement */}
          <motion.div {...fadeUp(0)} className="mb-6 md:mb-8">
            <span className="announce-pill inline-flex items-center gap-2">
              {/* Live pulse indicator */}
              <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
              </span>
              Now accepting enrollments for Batch 2025
              <span className="font-bold text-amber-500" aria-hidden>→</span>
            </span>
          </motion.div>

          {/* H1 — typography scale */}
          <motion.h1
            {...fadeUp(0.08)}
            className={cn(
              'font-bold tracking-tight text-foreground',
              'text-hero-display-lg',
              'mb-5 md:mb-6'
            )}
          >
            Empowering the Next
            <span className="mt-2 block gradient-text">Generation of Cloud &amp; AI Leaders</span>
          </motion.h1>

          {/* Lead paragraph */}
          <motion.p
            {...fadeUp(0.16)}
            className={cn(
              'text-hero-lead text-muted-foreground max-w-prose-narrow mx-auto',
              'mb-8 md:mb-10'
            )}
          >
            Industry-led training in Cloud Computing, AI, Data Science, and DevOps — with{' '}
            <strong className="font-semibold text-primary">100% placement assistance</strong> and
            expert mentorship.
          </motion.p>

          {/* CTAs — shadcn Button + consistent gap */}
          <motion.div
            {...fadeUp(0.24)}
            className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-10 md:mb-12"
          >
            <Button
              onClick={() => scrollToSection('courses')}
              className="btn-gradient h-11 px-6 py-3 text-sm font-semibold rounded-full gap-2"
            >
              Explore Courses
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => scrollToSection('register')}
              className="btn-outline h-11 px-6 py-3 text-sm font-semibold rounded-full"
            >
              Register Now
            </Button>
            <a
              href="https://wa.me/918179737527"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center justify-center gap-2 h-11 px-6 py-3 rounded-full',
                'text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200',
                'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
              )}
              aria-label="Chat on WhatsApp"
            >
              <FaWhatsapp size={18} />
              <span>+91 81797 37527</span>
            </a>
          </motion.div>

          {/* Stats — glass card, consistent spacing */}
          <motion.div
            {...fadeUp(0.32)}
            className={cn(
              'inline-flex flex-wrap items-center justify-center gap-8 md:gap-10',
              'px-7 py-5 md:px-10 md:py-6 rounded-2xl',
              'glass-card shadow-lg mb-12 md:mb-14',
              'ring-1 ring-teal-500/10 dark:ring-teal-400/10'
            )}
          >
            <StatBadge value="500+" label="Students Trained" />
            <div
              className="hidden sm:block w-px self-stretch bg-border min-h-[2rem]"
              aria-hidden
            />
            <StatBadge value="7+" label="Tech Domains" />
            <div
              className="hidden sm:block w-px self-stretch bg-border min-h-[2rem]"
              aria-hidden
            />
            <StatBadge value="100%" label="Placement Support" />
            <div
              className="hidden sm:block w-px self-stretch bg-border min-h-[2rem]"
              aria-hidden
            />
            <StatBadge value="1:1" label="Mentorship" />
          </motion.div>

          {/* Feature pills — responsive gap */}
          <motion.div
            {...fadeUp(0.4)}
            className="flex flex-wrap items-center justify-center gap-8 sm:gap-10 md:gap-12"
          >
            <FeaturePill icon={Brain} label="AI & GenAI" color="linear-gradient(135deg,#0d9488,#0f766e)" />
            <FeaturePill icon={Cloud} label="Cloud" color="linear-gradient(135deg,#f59e0b,#d97706)" />
            <FeaturePill icon={Zap} label="Data" color="linear-gradient(135deg,#06b6d4,#0284c7)" />
            <FeaturePill icon={Award} label="Career" color="linear-gradient(135deg,#8b5cf6,#6d28d9)" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
